import {
  CONSUMER_ERROR_CODES,
  CONSUMER_WEBHOOK_PREVIOUS_SECRET_HOURS,
  type Consumer,
  type ConsumerWebhook,
  type ConsumerWebhookUrlInput,
  type ConsumerWebhookWithSecret,
} from '@easyhelp/shared';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useState } from 'react';
import {
  useActivateConsumerWebhook,
  useConsumerWebhook,
  useDisableConsumerWebhook,
  useRotateConsumerWebhookSecret,
  useUpdateConsumerWebhookUrl,
} from '../../../api/consumer-webhooks';
import type { ApiError } from '../../../api/client';
import { ConfirmDialog } from '../../../components/ConfirmDialog';
import { formatDateTime } from '../format';
import { SigningSecretDialog } from './SigningSecretDialog';
import { WebhookDeliveries } from './WebhookDeliveries';
import { WebhookUrlForm } from './WebhookUrlForm';
import { WEBHOOK_STATUS_LABELS, urlRejectionOf, webhookStatus } from './webhook-format';

export interface ConsumerWebhookDialogProps {
  open: boolean;
  consumer: Consumer;
  onClose: () => void;
}

type Overlay =
  | { kind: 'none' }
  | { kind: 'rotate' }
  | { kind: 'disable' }
  | { kind: 'secret'; revealed: ConsumerWebhookWithSecret; rotated: boolean };

/** Errori dopo i quali la conferma non ha più senso: resta solo «Chiudi». */
const BLOCKING_CODES: readonly string[] = [CONSUMER_ERROR_CODES.webhookNotActive, CONSUMER_ERROR_CODES.alreadyRevoked];

/**
 * «Notifica di cambio stato» di un consumer (DEC-013, #85): attivazione con un indirizzo `https`,
 * modifica, rigenerazione del segreto, disattivazione, ultime consegne ed evento di prova. Per un
 * consumer revocato solo la lettura. Il segreto di firma si vede una volta, in `SigningSecretDialog`,
 * sopra questo dialogo; le conferme usano `ConfirmDialog`.
 */
export function ConsumerWebhookDialog({ open, consumer, onClose }: ConsumerWebhookDialogProps) {
  const state = useConsumerWebhook(consumer.id);
  const activate = useActivateConsumerWebhook(consumer.id);
  const updateUrl = useUpdateConsumerWebhookUrl(consumer.id);
  const rotate = useRotateConsumerWebhookSecret(consumer.id);
  const disable = useDisableConsumerWebhook(consumer.id);
  const [overlay, setOverlay] = useState<Overlay>({ kind: 'none' });
  const [notice, setNotice] = useState<string | null>(null);
  // Revocato da un altro amministratore mentre il dialogo è aperto: si passa alla sola lettura.
  const [revokedMeanwhile, setRevokedMeanwhile] = useState(false);

  const revoked = consumer.status === 'revocato' || revokedMeanwhile;
  const webhook = state.data?.webhook ?? null;
  const status = webhookStatus(webhook);
  // Durante una scrittura il dialogo non si chiude: smontato, perderebbe la risposta (e il segreto).
  const busy = activate.isPending || updateUrl.isPending || rotate.isPending || disable.isPending;

  const onActionError = (error: ApiError) => {
    if (error.code === CONSUMER_ERROR_CODES.alreadyRevoked) setRevokedMeanwhile(true);
  };

  const closeOverlay = () => {
    setOverlay({ kind: 'none' });
    rotate.reset();
    disable.reset();
  };

  const submitUrl = (input: ConsumerWebhookUrlInput) => {
    setNotice(null);
    activate.reset();
    updateUrl.reset();
    if (status === 'attiva') {
      updateUrl.mutate(input, {
        onSuccess: (saved) => setNotice(`Indirizzo salvato: i prossimi eventi vanno a ${saved.url}.`),
        onError: onActionError,
      });
      return;
    }
    activate.mutate(input, {
      onSuccess: (revealed) => {
        activate.reset();
        setNotice(`Notifica attiva: a ogni cambio di stato parte un evento verso ${revealed.url}.`);
        setOverlay({ kind: 'secret', revealed, rotated: false });
      },
      onError: onActionError,
    });
  };

  const confirmRotate = () =>
    rotate.mutate(undefined, {
      onSuccess: (revealed) => {
        rotate.reset();
        setNotice('Segreto di firma rigenerato.');
        setOverlay({ kind: 'secret', revealed, rotated: true });
      },
      onError: onActionError,
    });

  const confirmDisable = () =>
    disable.mutate(undefined, {
      onSuccess: () => {
        setNotice('Notifica disattivata: nessun nuovo evento parte e quelli in coda sono scartati.');
        closeOverlay();
      },
      onError: onActionError,
    });

  // L'errore resta visibile anche se, dopo un 409, lo stato riletto cambia il form.
  const urlFailure = activate.error ?? updateUrl.error;
  const urlError = urlRejectionOf(urlFailure);

  return (
    <>
      <Dialog
        open={open}
        onClose={busy ? undefined : onClose}
        fullWidth
        maxWidth="md"
        aria-labelledby="consumer-webhook-title"
      >
        <DialogTitle id="consumer-webhook-title">Notifica di cambio stato di {consumer.name}</DialogTitle>
        <DialogContent>
          <Stack spacing={4} sx={{ pt: 1 }}>
            <Typography color="text.secondary">
              Facoltativa: a ogni cambio di stato di un ticket dei suoi committenti EasyHelp invia al prodotto un evento
              firmato, con ritentativi per circa un giorno. Il prodotto può sempre leggere lo stato a richiesta.
            </Typography>

            {state.isPending && (
              <Stack direction="row" spacing={2} alignItems="center" role="status">
                <CircularProgress size={20} />
                <Typography>Caricamento della notifica…</Typography>
              </Stack>
            )}
            {state.isError && (
              <Alert
                severity="error"
                action={
                  <Button color="inherit" size="small" onClick={() => void state.refetch()}>
                    Riprova
                  </Button>
                }
              >
                Notifica non disponibile: {state.error.message}
              </Alert>
            )}

            {state.data && (
              <>
                <WebhookSummary webhook={webhook} revoked={revoked} />
                <Box role="status" aria-label="Esito dell’operazione">
                  {notice && (
                    <Alert severity="success" role="none" onClose={() => setNotice(null)}>
                      {notice}
                    </Alert>
                  )}
                </Box>
                {revoked ? (
                  <Alert severity="info">
                    Il consumer è revocato: la notifica non si configura più e nessun evento parte verso di lui.
                  </Alert>
                ) : (
                  <Stack spacing={3}>
                    <WebhookUrlForm
                      key={`${status}:${webhook?.url ?? ''}`}
                      defaultUrl={webhook?.url ?? ''}
                      submitLabel={
                        status === 'attiva'
                          ? 'Salva l’indirizzo'
                          : status === 'disattivata'
                            ? 'Riattiva la notifica'
                            : 'Attiva la notifica'
                      }
                      requireChange={status === 'attiva'}
                      rejection={urlError}
                      busy={busy}
                      onSubmit={submitUrl}
                    />
                    {urlFailure && !urlError && <Alert severity="error">{urlFailure.message}</Alert>}
                    {status === 'attiva' && (
                      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                        <Button variant="outlined" disabled={busy} onClick={() => setOverlay({ kind: 'rotate' })}>
                          Rigenera il segreto
                        </Button>
                        <Button
                          variant="outlined"
                          color="error"
                          disabled={busy}
                          onClick={() => setOverlay({ kind: 'disable' })}
                        >
                          Disattiva la notifica
                        </Button>
                      </Stack>
                    )}
                  </Stack>
                )}
                {webhook && (
                  <>
                    <Divider />
                    <WebhookDeliveries
                      consumerId={consumer.id}
                      canTest={status === 'attiva' && !revoked}
                      onTestError={onActionError}
                    />
                  </>
                )}
              </>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={busy}>
            Chiudi
          </Button>
        </DialogActions>
      </Dialog>

      {overlay.kind === 'rotate' && (
        <ConfirmDialog
          open
          title="Rigenerare il segreto di firma?"
          confirmLabel="Rigenera"
          error={rotate.error?.message}
          blocked={BLOCKING_CODES.includes(rotate.error?.code ?? '')}
          busy={rotate.isPending}
          onConfirm={confirmRotate}
          onClose={closeOverlay}
        >
          EasyHelp genera un segreto nuovo, visibile una sola volta. Il precedente resta valido per{' '}
          {CONSUMER_WEBHOOK_PREVIOUS_SECRET_HOURS} ore: intanto ogni evento porta entrambe le firme, poi vale solo il
          nuovo. Consegna il nuovo segreto al team del prodotto entro questo tempo.
        </ConfirmDialog>
      )}

      {overlay.kind === 'disable' && (
        <ConfirmDialog
          open
          title="Disattivare la notifica?"
          confirmLabel="Disattiva"
          confirmColor="error"
          error={disable.error?.message}
          blocked={BLOCKING_CODES.includes(disable.error?.code ?? '')}
          busy={disable.isPending}
          onConfirm={confirmDisable}
          onClose={closeOverlay}
        >
          Nessun nuovo evento parte verso {webhook?.url}; quelli in coda sono scartati e i segreti di firma cancellati.
          Il prodotto può sempre leggere lo stato dei ticket a richiesta. Se riattivi la notifica, EasyHelp genera un
          segreto nuovo.
        </ConfirmDialog>
      )}

      {overlay.kind === 'secret' && (
        <SigningSecretDialog
          open
          consumerName={consumer.name}
          revealed={overlay.revealed}
          rotated={overlay.rotated}
          onClose={closeOverlay}
        />
      )}
    </>
  );
}

/** Stato della notifica: indirizzo, segreto in uso, sovrapposizione con il precedente, disattivazione. */
function WebhookSummary({ webhook, revoked }: { webhook: ConsumerWebhook | null; revoked: boolean }) {
  const status = webhookStatus(webhook);
  const previousUntil = webhook?.previousSecretExpiresAt;

  return (
    <Stack spacing={1} alignItems="flex-start" aria-label="Stato della notifica" role="group">
      <Chip
        size="small"
        label={WEBHOOK_STATUS_LABELS[status]}
        color={status === 'attiva' ? 'success' : 'default'}
        variant={status === 'attiva' ? 'filled' : 'outlined'}
      />
      {!webhook && (
        <Typography variant="body2">
          Mai attivata: il prodotto legge lo stato dei ticket a richiesta.
          {!revoked && ' Attivala se il prodotto è raggiungibile da internet e vuole un evento a ogni cambio di stato.'}
        </Typography>
      )}
      {webhook?.enabled && (
        <>
          <Typography variant="body2">
            Gli eventi vanno a{' '}
            <Typography
              component="code"
              variant="body2"
              sx={{ fontFamily: (theme) => theme.typography.fontFamilyMonospace, wordBreak: 'break-all' }}
            >
              {webhook.url}
            </Typography>
          </Typography>
          {webhook.secretCreatedAt && (
            <Typography variant="body2" color="text.secondary">
              Segreto di firma in uso dal {formatDateTime(webhook.secretCreatedAt)}.
            </Typography>
          )}
          {previousUntil && (
            <Typography variant="body2" color="text.secondary">
              Il segreto precedente firma ancora fino al {formatDateTime(previousUntil)}.
            </Typography>
          )}
        </>
      )}
      {webhook && !webhook.enabled && (
        <Typography variant="body2">
          Disattivata
          {webhook.disabledAt ? ` il ${formatDateTime(webhook.disabledAt)}` : ''}
          {webhook.disabledBy ? ` da ${webhook.disabledBy}` : ''}. Ultimo indirizzo:{' '}
          <Typography
            component="code"
            variant="body2"
            sx={{ fontFamily: (theme) => theme.typography.fontFamilyMonospace, wordBreak: 'break-all' }}
          >
            {webhook.url}
          </Typography>
        </Typography>
      )}
    </Stack>
  );
}
